import { useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Badge } from "@jambushrushti/components/ui/badge";
import { FeedbackQRCode } from "@jambushrushti/components/shared/FeedbackQRCode";
import {
  Library as LibraryIcon,
  Search,
  BookOpen,
  Scroll as ScrollIcon,
  MapPin,
  Calendar,
  User,
  Info,
  ArrowRight,
} from "lucide-react";
import { useLibraryCatalog } from "@jambushrushti/hooks/useLibraryCatalog";

const Library = () => {
  const {
    data: catalogItems = [],
    isLoading,
    error,
    refetch,
  } = useLibraryCatalog();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const filteredItems = catalogItems.filter(item => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !selectedType || item.type === selectedType;
    return matchesSearch && matchesType;
  });

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-5 sm:py-8 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <div className="animate-fade-up mb-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs sm:mb-6 sm:px-4 sm:py-2 sm:text-sm">
              <LibraryIcon className="h-3.5 w-3.5 text-primary sm:h-4 sm:w-4" />
              <span className="text-primary">
                Muni Jambuvijay Research Center
              </span>
            </div>
            <h1 className="font-heading animate-fade-up mb-3 text-3xl font-bold text-foreground delay-100 sm:mb-6 sm:text-5xl">
              Library Catalog
            </h1>
            <p className="font-body animate-fade-up text-foreground/80 delay-200">
              Browse our extensive collection of Jain books, manuscripts, and
              scholarly resources. Located at Shantigram.
            </p>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="border-y border-border bg-secondary/50 py-4">
        <div className="container mx-auto px-4">
          <div className="font-body flex items-center justify-center gap-2 text-muted-foreground">
            <Info className="h-4 w-4" />
            <span>
              This is a view-only catalog. For library access, please contact
              the librarian.
            </span>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="sticky top-16 z-40 border-b border-border bg-card py-3 sm:py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-3 sm:gap-4 md:flex-row">
            <div className="relative w-full flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by title, author, or category..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={selectedType === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType(null)}
              >
                All
              </Button>
              <Button
                variant={selectedType === "Book" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("Book")}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                Books
              </Button>
              <Button
                variant={selectedType === "Manuscript" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("Manuscript")}
              >
                <ScrollIcon className="mr-2 h-4 w-4" />
                Manuscripts
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Catalog Grid */}
      <section className="bg-background py-6 sm:py-10">
        <div className="container mx-auto px-4">
          {isLoading && (
            <p className="py-12 text-center text-muted-foreground">
              Loading books...
            </p>
          )}
          {error && (
            <div role="alert" className="space-y-4 py-12 text-center">
              <p className="text-destructive">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          )}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredItems.map((item, index) => (
              <Link key={item.id} to={`/library/${item.id}`} className="group">
                <Card
                  variant="interactive"
                  className="animate-fade-up h-full overflow-hidden bg-card"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Cover Image */}
                  <div className="relative aspect-[3/4] overflow-hidden bg-secondary/50">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute left-3 right-3 top-3 flex items-start justify-between">
                      <Badge
                        variant={item.type === "Book" ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {item.type === "Book" ? (
                          <BookOpen className="mr-1 h-3 w-3" />
                        ) : (
                          <ScrollIcon className="mr-1 h-3 w-3" />
                        )}
                        {item.type}
                      </Badge>
                      <Badge
                        variant={item.available ? "outline" : "secondary"}
                        className={`text-xs ${item.available ? "border-primary bg-card/90 text-primary" : "bg-card/90"}`}
                      >
                        {item.available === null
                          ? "Availability not specified"
                          : item.available
                            ? "Available"
                            : "Reference Only"}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading line-clamp-2 text-lg transition-colors group-hover:text-primary">
                      {item.title}
                    </CardTitle>
                    <div className="font-body font-medium text-primary">
                      {item.category}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="font-body space-y-1.5 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5" />
                        <span className="line-clamp-1">{item.author}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>{item.year}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{item.location}</span>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                      <Badge variant="outline">{item.language}</Badge>
                      <span className="font-body flex items-center gap-1 text-primary opacity-0 transition-opacity group-hover:opacity-100">
                        View Details <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {!isLoading && !error && filteredItems.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                No items found matching your search.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-gradient-spiritual py-8 sm:py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading mb-4 text-2xl font-bold text-foreground">
            Visit Our Library
          </h2>
          <p className="font-body mx-auto mb-6 max-w-lg text-muted-foreground sm:mb-8">
            Located at Muni Jambuvijay Research Center, Shantigram. Contact us
            to schedule a visit or inquire about specific materials.
          </p>
          <div className="mx-auto mb-6 grid max-w-md grid-cols-2 gap-2.5 sm:mb-8 sm:flex sm:max-w-none sm:flex-wrap sm:justify-center sm:gap-4">
            <Button
              variant="hero"
              asChild
              className="w-full px-2.5 text-xs sm:w-auto sm:px-6 sm:text-sm"
            >
              <a href="/contact" className="justify-center">
                Contact Library
              </a>
            </Button>
            <Button
              variant="outline"
              asChild
              className="w-full px-2.5 text-xs sm:w-auto sm:px-6 sm:text-sm"
            >
              <a
                href="https://maps.app.goo.gl/mk3KCxWMSVLmXYiC6"
                target="_blank"
                rel="noopener noreferrer"
                className="justify-center"
              >
                <MapPin className="mr-1.5 h-3.5 w-3.5 shrink-0 sm:mr-2 sm:h-4 sm:w-4" />
                Get Directions
              </a>
            </Button>
          </div>

          {/* Feedback QR Code */}
          <div className="mx-auto max-w-xs">
            <FeedbackQRCode />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Library;
